import React, {useId, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Button, Separator, Text, useSlotRecipe} from '@chakra-ui/react3'
import ModuleContainer from '../../components/cms/module-container'
import Heading from '../../components/cms/heading'
import Image from '../../components/cms/image'
import productColorsSlotRecipe from './index.recipe'
import useSharedLibraryAssetUrl from '../../../hooks/useSharedLibraryAssetUrl'
import {hasValidImageSrc} from '../../utils/utils'
import {FormattedMessage} from 'react-intl'

/**
 * Product Colors module displays a color palette exploration interface with interactive color swatches
 * and corresponding product images. Users can select different colors to view how the product looks
 * in each available color variant.
 *
 * @component
 * @param {string} [anchor] - Optional anchor ID for deep linking to this module
 * @param {'default'|'subtle'|'strong'|'inverse'} [background='default'] - Background style for the module
 * @param {object} [heading] - Optional heading configuration object with subline, headline1, headline2, etc.
 * @param {number} [headingLevel=2] - HTML heading level (1-6) for semantic structure
 * @param {object} colorProduct - Color product configuration object containing:
 *   @param {string} colorProduct.productSetName - Product set name used to construct image paths (e.g., "YOYO_FRAME")
 *   @param {Array<object>} colorProduct.colors - Array of color objects, each containing:
 *     @param {string} colorProduct.colors[].name - Display name of the color (e.g., "Peppermint")
 *     @param {string} colorProduct.colors[].colorCode - Color code used in image filename (e.g., "C1E4D4")
 *     @param {string} colorProduct.colors[].swatch - Swatch image URL
 *     @param {string} colorProduct.colors[].alt - Title/alt text for the swatch
 * @param {object} [multiColorImage] - Optional multi-color image configuration displayed as first swatch
 *   @param {string} [multiColorImage.src] - Multi-color product image URL
 *   @param {string} [multiColorImage.disBaseLink] - DIS base link for image
 *   @param {string} [multiColorImage.title] - Image title
 *   @param {string} [multiColorImage.alt] - Image alt text
 * @param {'20'|'30'|'40'|'50'} [surfaceHeight='20'] - Height percentage of the background surface gradient (20%, 30%, 40%, or 50%)
 * @returns {JSX.Element} Rendered ProductColors module with interactive color selection
 */
const ProductColors = ({
    moduleId,
    moduleType,
    pageId,
    anchor,
    background,
    heading,
    headingLevel = 2,
    colorProduct: {colors, productSetName},
    multiColorImage,
    surfaceHeight = '20'
}) => {
    const recipe = useSlotRecipe({recipe: productColorsSlotRecipe})
    const styles = useMemo(() => recipe({surfaceHeight}), [recipe, surfaceHeight])
    const imagesPrefix = useSharedLibraryAssetUrl(
        useMemo(
            () => `cms-modules/product-colors/${productSetName.toLowerCase().replace('_', '-')}`,
            [productSetName]
        )
    )
    const hasMultiColorImage = hasValidImageSrc(multiColorImage)
    const idPrefix = useId()

    // -1 means the multi color image, otherwise the index from colorProduct.colors array
    const [selectedVariant, setSelectedVariant] = useState(hasMultiColorImage ? -1 : 0)
    const selectedVariantImgAlt = useMemo(() => {
        if (selectedVariant === -1) {
            return
        }
        const swatchAlt = colors[selectedVariant].alt
        // the swatch alt has the product name, material and color and at the end ", swatch" or "swatch"
        // we should remove that to have the alt for the image
        return swatchAlt.replace(/,?\s*swatch\s*$/i, '').trim()
    }, [colors, selectedVariant])

    return (
        <ModuleContainer
            moduleId={moduleId}
            moduleType={moduleType}
            pageId={pageId}
            anchor={anchor}
            background={background}
            css={styles.root}
            spacing="none"
        >
            <Heading {...heading} headingLevel={headingLevel} alignment="center" />
            <Box css={styles.bgSurface} />
            <Box css={styles.spotlightLight}>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="848"
                    height="991"
                    viewBox="0 0 848 991"
                    fill="none"
                >
                    <g opacity="0.04" filter="url(#filter0_f_9520_813)">
                        <path d="M120 871L417.545 120H433.918L728 871H120Z" fill="white" />
                    </g>
                    <defs>
                        <filter
                            id="filter0_f_9520_813"
                            x="0"
                            y="0"
                            width="848"
                            height="991"
                            filterUnits="userSpaceOnUse"
                            colorInterpolationFilters="sRGB"
                        >
                            <feFlood floodOpacity="0" result="BackgroundImageFix" />
                            <feBlend
                                mode="normal"
                                in="SourceGraphic"
                                in2="BackgroundImageFix"
                                result="shape"
                            />
                            <feGaussianBlur
                                stdDeviation="60"
                                result="effect1_foregroundBlur_9520_813"
                            />
                        </filter>
                    </defs>
                </svg>
            </Box>
            <Box css={styles.spotlightDark}>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="666"
                    height="911"
                    viewBox="0 0 666 911"
                    fill="none"
                >
                    <g opacity="0.04" filter="url(#filter0_f_6013_16672)">
                        <path d="M-62 791L235.545 40H251.918L546 791H-62Z" fill="white" />
                    </g>
                    <defs>
                        <filter
                            id="filter0_f_6013_16672"
                            x="-182"
                            y="-80"
                            width="848"
                            height="991"
                            filterUnits="userSpaceOnUse"
                            colorInterpolationFilters="sRGB"
                        >
                            <feFlood floodOpacity="0" result="BackgroundImageFix" />
                            <feBlend
                                mode="normal"
                                in="SourceGraphic"
                                in2="BackgroundImageFix"
                                result="shape"
                            />
                            <feGaussianBlur
                                stdDeviation="60"
                                result="effect1_foregroundBlur_6013_16672"
                            />
                        </filter>
                    </defs>
                </svg>
            </Box>
            <Box css={styles.imagesContainer}>
                {hasMultiColorImage && (
                    <Box
                        css={[
                            styles.imageContainer,
                            selectedVariant === -1
                                ? styles.imageContainerActive
                                : styles.imageContainerInactive
                        ]}
                    >
                        <Image
                            {...multiColorImage}
                            objectFit="contain"
                            widths={{base: '97vw', s: '445px'}}
                            loading={selectedVariant === -1 ? 'eager' : 'lazy'}
                            fetchpriority={selectedVariant === -1 ? 'high' : 'auto'}
                        />
                    </Box>
                )}
                {colors.map((color, index) => (
                    <Box
                        key={index}
                        css={[
                            styles.imageContainer,
                            selectedVariant === index
                                ? styles.imageContainerActive
                                : styles.imageContainerInactive
                        ]}
                    >
                        <Image
                            src={`${imagesPrefix}/${color.colorCode}.png`}
                            alt={selectedVariantImgAlt}
                            objectFit="contain"
                            widths={{base: '97vw', s: '445px'}}
                            loading={selectedVariant === index ? 'eager' : 'lazy'}
                            fetchpriority={selectedVariant === index ? 'high' : 'auto'}
                        />
                    </Box>
                ))}
            </Box>
            <Box css={styles.swatchesContainer}>
                <Text css={styles.labelContainer}>
                    {selectedVariant >= 0 ? (
                        <>
                            <Text as="span" css={styles.label}>
                                Color:&nbsp;
                            </Text>
                            <Text as="span" css={styles.value}>
                                {colors[selectedVariant].name}
                            </Text>
                        </>
                    ) : (
                        // need to render something to prevent layout shift when multi-color is selected
                        // maybe this will be updated to render a specific label with followup ticket
                        <>&nbsp;</>
                    )}
                </Text>
                <Box css={styles.swatches} data-testid="swatches">
                    {/* Multi-color swatch appears first when multiColorImage is provided */}
                    {hasMultiColorImage && (
                        <>
                            <Button
                                className="leap-swatch group"
                                unstyled
                                css={styles.swatchTouchArea}
                                {...(selectedVariant === -1 && {'data-active': true})}
                                onClick={() => setSelectedVariant(-1)}
                                // need the labelledby because the label is hidden on mobile
                                aria-labelledby={`${idPrefix}-multi-color-label`}
                            >
                                <Text css={styles.swatchLabel} id={`${idPrefix}-multi-color-label`}>
                                    <FormattedMessage
                                        id="leap.product-colors.swatch.multi-color.label"
                                        defaultMessage="Multi-color"
                                    />
                                </Text>
                                <Box css={styles.multiColorSwatch} />
                            </Button>
                            {/* Visual separator between multi-color swatch and individual colors */}
                            <Box css={styles.separatorContainer}>
                                <Separator
                                    variant="solid"
                                    orientation={{base: 'vertical', s: 'horizontal'}}
                                    size="sm"
                                    css={styles.separator}
                                />
                            </Box>
                        </>
                    )}
                    {colors.map((color, index) => (
                        // the swatch has very specific implementation to allow for desktop label and extracting reusable component will make it more complicated.
                        <Button
                            key={index}
                            className="leap-swatch group"
                            unstyled
                            css={styles.swatchTouchArea}
                            {...(index === selectedVariant && {'data-active': true})}
                            onClick={() => setSelectedVariant(index)}
                            // need the labelledby because the label is hidden on mobile
                            aria-labelledby={`${idPrefix}-color-label-${index}`}
                        >
                            <Text css={styles.swatchLabel} id={`${idPrefix}-color-label-${index}`}>
                                {color.name || color.alt}
                            </Text>
                            <Box css={styles.swatch}>
                                <Image
                                    alt={color.alt}
                                    src={color.swatch}
                                    cursor="inherit"
                                    objectFit="cover"
                                    widths={{base: '26px'}}
                                />
                            </Box>
                        </Button>
                    ))}
                </Box>
            </Box>
        </ModuleContainer>
    )
}

ProductColors.propTypes = {
    anchor: PropTypes.string,
    background: PropTypes.string,
    colorProduct: PropTypes.shape({
        productSetName: PropTypes.string,
        colors: PropTypes.objectOf(
            PropTypes.shape({
                colorCode: PropTypes.string,
                swatch: PropTypes.string,
                name: PropTypes.string,
                alt: PropTypes.string
            })
        )
    }),
    heading: PropTypes.object,
    headingLevel: PropTypes.number,
    multiColorImage: PropTypes.shape({
        src: PropTypes.string,
        disBaseLink: PropTypes.string,
        title: PropTypes.string,
        alt: PropTypes.string
    }),
    surfaceHeight: PropTypes.string,
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string
}

export default ProductColors
